import type { ReactNode } from "react";
import { PosterBox, PosterMark, RegistrationMarks } from "../ui/poster";
import { XHandleLink } from "../ui/x-link";
import { difficultyLine } from "./intake";
import type { PublicDoor } from "../../lib/api";

/** The card's own eyebrow. "A door" — the same word the printed page uses. */
export const DOOR_KICKER = "A door";
export const ABOUT_KICKER = "About";
export const GETS_THROUGH_KICKER = "What gets through";
export const DOOR_TAGLINE = "A Nob is a doorman, not an inbox.";
export const COPY_LINK_LABEL = "Copy this door's link";
export const LINK_COPIED = "Link copied";

/**
 * A door with no bio still says whose door it is. The persona is the honest
 * substitute — it is a published fact about the door, not a line invented on
 * the owner's behalf — and a door that has neither says the plainest true
 * thing rather than leaving the box empty.
 */
export function aboutLine(door: PublicDoor): string {
  if (door.bio?.trim()) return door.bio.trim();
  if (door.persona_name?.trim()) return `${door.persona_name.trim()} answers this door.`;
  return "This door reads the knock before it opens.";
}

/** `nob.me/@handle` — the address as a person would write it down. */
export function doorUrl(handle: string): string {
  return `nob.me/@${handle.replace(/^@/, "")}`;
}

/**
 * THE DOOR PLATE, IN THE APP — the same object the server prints at
 * `nob.me/@handle` (`apps/api/src/pages/door.ts`), rendered by the SPA for the
 * visit that happens inside it.
 *
 * WHY THIS EXISTS AT ALL. The signed-out visit used to be a chat: a header bar,
 * then the door's description as a run of doorman bubbles, then a composer. Two
 * things were wrong with it. The first is that it was a DIFFERENT OBJECT from
 * the printed page a stranger had just been looking at a moment earlier — same
 * address, same door, two unrelated designs, and the one they arrived from is
 * the one they were told to trust. The second is that the bubble version never
 * showed the owner's BIO at all: the informational lines were assembled by hand
 * here rather than by the intake's own builders, and the bio was simply missing
 * from the list. A stranger deciding whether to knock could not read the one
 * sentence the owner wrote about themselves.
 *
 * SO THE SECTIONS ARE THE PRINTED PAGE'S SECTIONS, in the printed page's order:
 * kicker, wordmark, who, About, the public line, the difficulty line, the
 * action, the footer. Anything that appears on one and not the other is a
 * mismatch to be fixed rather than a variation to be enjoyed — including the
 * absent block: a door with no public line prints no box on either surface.
 *
 * WHAT IS DELIBERATELY NOT ON IT. No follower count, no employer, no anything
 * else the identity port happens to know: the door publishes what its owner
 * named, and the allowlist discipline that governs the printed page (ruling C6)
 * governs this one for the same reason. No money figure either, ever — the
 * difficulty is prose and nothing else is money (ruling CR, §1.5).
 *
 * The ACTION is the caller's, because it is the one thing that genuinely
 * differs by who is standing there: a stranger writes and signs in, a signed-in
 * knocker writes and knocks, and the owner is told this is their own plate.
 */
export function DoorCard({
  door,
  children,
  onCopyLink,
}: {
  door: PublicDoor;
  /** The action: a form, a button, or whatever this visitor is owed. */
  children: ReactNode;
  onCopyLink?: () => void;
}) {
  const difficulty = difficultyLine(door);
  const handle = door.handle.replace(/^@/, "");

  return (
    <div className="nob-poster-card">
      <RegistrationMarks />
      <p className="nob-poster-card-kicker">{DOOR_KICKER}</p>
      <PosterMark />

      <div className="nob-poster-who">
        {door.avatar && (
          <img src={door.avatar} alt="" width={48} height={48} loading="lazy" decoding="async" />
        )}
        <p className="nob-poster-name">{door.display_name}</p>
        {/* The handle goes to the profile behind it (owner, 2026-08-25) — the
            plate is a stranger's first look at whoever owns this door, and the
            handle was the one fact on it that led nowhere. Kept identical to
            the server's own printing of this plate (`api/src/pages/door.ts`). */}
        <p className="nob-poster-handle">
          <XHandleLink handle={handle} />
        </p>
      </div>

      <PosterBox kicker={ABOUT_KICKER}>
        <p>{aboutLine(door)}</p>
      </PosterBox>

      {/* WHAT GETS THROUGH, AS STRANGERS READ IT (ruling R-0910-10): the
          owner's own public line, and nothing out of their House Rules — that
          document instructs the doorman and is published nowhere. A door with
          no line gets NO BOX, exactly as the printed plate gives it none:
          there is nothing true to put in it, and the plate still says who this
          is and how hard the door is. */}
      {door.public_line && (
        <PosterBox kicker={GETS_THROUGH_KICKER}>
          <p>{door.public_line}</p>
        </PosterBox>
      )}

      {/* Prose, and never a figure (ruling CR). The server decides the sentence;
          this side computes nothing and has nothing to compute it from. */}
      {difficulty && <p className="nob-poster-aside">{difficulty}</p>}

      {children}

      <div className="nob-poster-foot">
        {onCopyLink ? (
          <button
            type="button"
            className="nob-poster-url"
            aria-label={COPY_LINK_LABEL}
            onClick={onCopyLink}
          >
            {doorUrl(handle)}
          </button>
        ) : (
          <span className="nob-poster-url">{doorUrl(handle)}</span>
        )}
        <span className="nob-poster-tag">{DOOR_TAGLINE}</span>
      </div>
    </div>
  );
}
